import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "../store/useStore";
import { Btn, Field, Icon, Input, Spinner, Toaster } from "../components/ui";
import { api, ApiError } from "../api/http";

// ============================================================
// /reset?token=… — set a new password from the reset mail link
// ============================================================

export function ResetScreen() {
  const lang = useStore((s) => s.lang);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const de = lang === "de";

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 8) {
      setErr(de ? "Mindestens 8 Zeichen." : "At least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setErr(de ? "Passwörter stimmen nicht überein." : "Passwords do not match.");
      return;
    }
    setBusy(true);
    void api
      .resetPassword(token, pw)
      .then(() => setDone(true))
      .catch((e2) =>
        setErr(
          e2 instanceof ApiError && e2.status === 400
            ? de
              ? "Link ungültig oder abgelaufen — bitte neuen anfordern."
              : "Link invalid or expired — please request a new one."
            : String(e2),
        ),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", padding: 20 }}>
      <div className="anim-pop" style={{ width: "min(440px, 100%)", background: "var(--surface)", border: "1px solid var(--glass-border)", borderRadius: 22, padding: "34px 32px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>S</div>
          <span style={{ fontSize: 16.5, fontWeight: 700 }}>SpielplatzGIS</span>
        </div>
        {!done ? (
          <>
            <h2 style={{ fontSize: 24 }}>{de ? "Neues Passwort vergeben" : "Set a new password"}</h2>
            <p style={{ color: "var(--text-mute)", marginTop: 7, marginBottom: 24, fontSize: 13.5 }}>
              {de ? "Der Link aus der E-Mail ist 60 Minuten gültig." : "The link from the email is valid for 60 minutes."}
            </p>
            {!token && (
              <p style={{ color: "var(--error)", fontSize: 13, marginBottom: 16 }}>
                {de ? "Kein Token in der URL — bitte den Link aus der E-Mail verwenden." : "No token in the URL — please use the link from the email."}
              </p>
            )}
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label={de ? "Neues Passwort" : "New password"}>
                <div style={{ position: "relative" }}>
                  <Input icon="lock-key" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} required autoFocus minLength={8} placeholder="••••••••" />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-mute)", padding: 6 }}
                  >
                    <Icon name={show ? "eye-slash" : "eye"} size={17} />
                  </button>
                </div>
              </Field>
              <Field label={de ? "Passwort wiederholen" : "Repeat password"}>
                <Input icon="lock-key" type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} placeholder="••••••••" />
              </Field>
              {err && (
                <p style={{ color: "var(--error)", fontSize: 12.5, display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon name="warning-circle" size={15} weight="fill" />
                  {err}
                </p>
              )}
              <Btn type="submit" size="lg" disabled={busy || !token}>
                {busy ? <Spinner color="#fff" /> : <Icon name="check" size={18} />}
                {de ? "Passwort speichern" : "Save password"}
              </Btn>
            </form>
          </>
        ) : (
          <div className="anim-pop" style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ width: 64, height: 64, borderRadius: 18, background: "var(--accent-soft)", color: "var(--accent-text)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <Icon name="check-circle" size={32} weight="fill" />
            </div>
            <h2 style={{ fontSize: 22 }}>{de ? "Passwort geändert" : "Password changed"}</h2>
            <p style={{ color: "var(--text-mute)", marginTop: 10, marginBottom: 24, fontSize: 13.5 }}>
              {de ? "Sie können sich jetzt mit dem neuen Passwort anmelden." : "You can now sign in with your new password."}
            </p>
            <Btn size="lg" onClick={() => navigate("/login")}>
              <Icon name="sign-in" size={18} />
              {de ? "Zur Anmeldung" : "Go to sign in"}
            </Btn>
          </div>
        )}
      </div>
      <Toaster />
    </div>
  );
}
